import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../api";
import { PageHeader, EmptyState, Modal, Field, Button, inr } from "../components/UI";
import Typeahead from "../components/Typeahead";
import { Receipt, Plus, Trash2, Download, Eye } from "lucide-react";
import { toast } from "sonner";

const searchCustomersAsync = async (q) => {
  const url = q ? `/customers?q=${encodeURIComponent(q)}&limit=50` : "/customers?limit=50";
  const r = await api.get(url);
  return r.data;
};

function PayBadge({ status }) {
  const map = { paid: "badge-paid", partial: "badge-partial", pending: "badge-pending" };
  return <span className={`badge ${map[status] || "badge-pending"}`}>{status}</span>;
}

export default function Invoices() {
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", items: [], tax_percent: 18, notes: "" });
  const [filter, setFilter] = useState("all");
  const [selectedCustomerLabel, setSelectedCustomerLabel] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [i, p] = await Promise.all([api.get("/invoices"), api.get("/products")]);
      setList(i.data); setProducts(p.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addItem = () => setForm({ ...form, items: [...form.items, { product_id: "", product_name: "", quantity: 1, unit_price: 0 }] });
  const updateItem = (i, k, v) => {
    const items = [...form.items];
    items[i] = { ...items[i], [k]: v };
    if (k === "product_id" && v) {
      const p = products.find((x) => x.id === v);
      if (p) { items[i].product_name = p.name; items[i].unit_price = p.default_price; }
    }
    setForm({ ...form, items });
  };
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const subtotal = form.items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const tax = subtotal * (Number(form.tax_percent) || 0) / 100;

  const openNew = () => {
    setForm({ customer_id: "", items: [{ product_id: "", product_name: "", quantity: 1, unit_price: 0 }], tax_percent: 18, notes: "" });
    setSelectedCustomerLabel("");
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.customer_id || form.items.length === 0) { toast.error("Customer and items required"); return; }
    try {
      const payload = { ...form, tax_percent: Number(form.tax_percent), items: form.items.map((i) => ({ ...i, quantity: Number(i.quantity), unit_price: Number(i.unit_price) })) };
      await api.post("/invoices", payload);
      toast.success("Invoice created"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete ${inv.invoice_no}?`)) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadPdf = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_no}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = filter === "all" ? list : list.filter((i) => i.payment_status === filter);

  return (
    <div className="p-4 lg:p-8 max-w-7xl">
      <PageHeader
        title="Invoices"
        subtitle="Bill customers, generate PDFs, and track payments"
        action={<Button onClick={openNew} data-testid="add-invoice-btn"><Plus size={16}/>New Invoice</Button>}
      />

      <div className="flex gap-2 mb-5" data-testid="invoice-filters">
        {["all", "pending", "partial", "paid"].map((s) => (
          <button key={s} data-testid={`inv-filter-${s}`} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider font-semibold transition ${filter === s ? "bg-[#C1121F] text-white" : "bg-white border border-[#E5E7EB] text-[#475569] hover:border-[#C1121F]"}`}>
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#475569]">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Receipt} title="No invoices yet" hint="Generate invoices from orders or create one manually."
          action={<Button onClick={openNew}><Plus size={16}/> New Invoice</Button>} />
      ) : (
        <div className="card-flat overflow-hidden">
          <table className="crm-table" data-testid="invoices-table">
            <thead>
              <tr>
                <th>Invoice #</th><th>Customer</th><th>Date</th>
                <th className="text-right">Total</th><th className="text-right">Paid</th><th className="text-right">Due</th>
                <th>Status</th><th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                  <td className="font-mono-num font-semibold text-[#C1121F]">{inv.invoice_no}</td>
                  <td>{inv.customer_name}</td>
                  <td className="text-xs text-[#475569]">{inv.created_at.slice(0, 10)}</td>
                  <td className="text-right font-mono-num font-semibold">{inr(inv.total)}</td>
                  <td className="text-right font-mono-num text-green-700">{inr(inv.paid_amount)}</td>
                  <td className="text-right font-mono-num text-[#C1121F]">{inr(inv.total - inv.paid_amount)}</td>
                  <td><PayBadge status={inv.payment_status} /></td>
                  <td className="text-right">
                    <Link to={`/invoices/${inv.id}`} data-testid={`view-invoice-${inv.id}`} className="inline-block p-1.5 text-[#475569] hover:text-[#C1121F]"><Eye size={15}/></Link>
                    <button onClick={() => downloadPdf(inv)} data-testid={`download-invoice-${inv.id}`} className="p-1.5 text-[#475569] hover:text-[#C1121F]"><Download size={15}/></button>
                    <button onClick={() => remove(inv)} data-testid={`delete-invoice-${inv.id}`} className="p-1.5 text-[#475569] hover:text-red-600"><Trash2 size={15}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New Invoice" maxWidth="max-w-3xl">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Customer" required>
              <Typeahead
                loadOptions={searchCustomersAsync}
                value={form.customer_id}
                selectedLabel={selectedCustomerLabel}
                onChange={(id, opt) => { setForm({...form, customer_id: id}); setSelectedCustomerLabel(opt?.name || ""); }}
                placeholder="Type to search customer…"
                required
                testid="inv-customer"
                renderMeta={(c) => `${c.type || ""} ${c.phone ? "· " + c.phone : ""} ${c.city ? "· " + c.city : ""}`}
              />
            </Field>
            <Field label="Tax % (GST)">
              <input data-testid="inv-tax" type="number" step="0.01" value={form.tax_percent} onChange={(e) => setForm({...form, tax_percent: e.target.value})} className="input-base" />
            </Field>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Items</label>
              <Button type="button" variant="secondary" onClick={addItem}><Plus size={14}/> Add item</Button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-4">
                    <Typeahead
                      options={products}
                      value={it.product_id || ""}
                      onChange={(id) => updateItem(i, "product_id", id)}
                      placeholder="Search product…"
                      testid={`inv-product-${i}`}
                    />
                  </div>
                  <input placeholder="Name" value={it.product_name} onChange={(e) => updateItem(i, "product_name", e.target.value)} required className="input-base col-span-3" />
                  <input type="number" min="1" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="input-base col-span-2" />
                  <input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className="input-base col-span-2" />
                  <button type="button" onClick={() => removeItem(i)} className="col-span-1 text-red-500 p-2"><Trash2 size={16}/></button>
                </div>
              ))}
            </div>
          </div>
          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="input-base" rows={2} />
          </Field>
          <div className="flex items-center justify-between pt-3 border-t border-[#E5E7EB]">
            <div className="text-sm text-[#475569] space-y-0.5">
              <div>Subtotal: <span className="font-mono-num font-semibold">{inr(subtotal)}</span></div>
              <div>Tax: <span className="font-mono-num font-semibold">{inr(tax)}</span></div>
              <div className="text-[#C1121F] font-bold text-lg font-mono-num">Total: {inr(subtotal + tax)}</div>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-invoice-btn">Create Invoice</Button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
